import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import ModuleGate from "@/components/base/ModuleGate";

/* ─── Types ──────────────────────────────────────────────────── */
type DomainRequest = {
  id: string;
  tenant_id: string;
  requested_domain: string;
  status: "pending" | "approved" | "rejected" | "active" | "cancelled";
  admin_notes: string | null;
  price_monthly: number;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
};

type TenantDomainInfo = {
  custom_domain: string | null;
  custom_domain_enabled: boolean;
  custom_domain_status: string | null;
};

/* ─── Status config ──────────────────────────────────────────── */
const STATUS_CONFIG: Record<string, {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
  desc: string;
}> = {
  pending: {
    label:  "Pending Review",
    color:  "text-amber-700",
    bg:     "bg-amber-50",
    border: "border-amber-200",
    icon:   "ri-time-line",
    desc:   "Your request is waiting for review.",
  },
  approved: {
    label:  "Approved",
    color:  "text-sky-700",
    bg:     "bg-sky-50",
    border: "border-sky-200",
    icon:   "ri-checkbox-circle-line",
    desc:   "Approved! DNS configuration is in progress.",
  },
  rejected: {
    label:  "Rejected",
    color:  "text-red-600",
    bg:     "bg-red-50",
    border: "border-red-200",
    icon:   "ri-close-circle-line",
    desc:   "Your request was rejected.",
  },
  active: {
    label:  "Active",
    color:  "text-emerald-700",
    bg:     "bg-emerald-50",
    border: "border-emerald-200",
    icon:   "ri-global-line",
    desc:   "Your custom domain is live and active.",
  },
  cancelled: {
    label:  "Cancelled",
    color:  "text-slate-500",
    bg:     "bg-slate-50",
    border: "border-slate-200",
    icon:   "ri-prohibited-line",
    desc:   "This request was cancelled.",
  },
};

/* ─── Props ──────────────────────────────────────────────────── */
interface Props {
  tenantId:   string;
  tenantSlug: string | null;
}

/* ─── Component ──────────────────────────────────────────────── */
export default function DomainTab({ tenantId, tenantSlug }: Props) {
  const [requests, setRequests]     = useState<DomainRequest[]>([]);
  const [domainInfo, setDomainInfo] = useState<TenantDomainInfo | null>(null);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [domain, setDomain]         = useState("");
  const [domainError, setDomainError] = useState("");

  /* ── Fetch requests + tenant domain info ── */
  const fetchData = useCallback(async () => {
    const [reqRes, tenantRes] = await Promise.all([
      supabase
        .from("tenant_domain_requests")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false }),
      supabase
        .from("saas_tenants")
        .select("custom_domain, custom_domain_enabled, custom_domain_status")
        .eq("id", tenantId)
        .maybeSingle(),
    ]);
    setRequests((reqRes.data as DomainRequest[]) ?? []);
    setDomainInfo(tenantRes.data as TenantDomainInfo | null);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── Validate domain ── */
  const validateDomain = (val: string): string => {
    const t = val.trim().toLowerCase();
    if (!t) return "Domain is required.";
    if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z]{2,})+$/.test(t))
      return "Enter a valid domain like yourstudio.com";
    return "";
  };

  /* ── Submit request ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateDomain(domain);
    if (err) { setDomainError(err); return; }
    setSubmitting(true);
    const { data, error } = await supabase
      .from("tenant_domain_requests")
      .insert({
        tenant_id:        tenantId,
        requested_domain: domain.trim().toLowerCase(),
        status:           "pending",
        price_monthly:    10,
      })
      .select()
      .maybeSingle();
    if (error) {
      setDomainError(error.message);
    } else if (data) {
      setRequests((prev) => [data as DomainRequest, ...prev]);
      setDomain("");
      setDomainError("");
    }
    setSubmitting(false);
  };

  /* ── Cancel request ── */
  const handleCancel = async (req: DomainRequest) => {
    setCancelling(req.id);
    const { data } = await supabase
      .from("tenant_domain_requests")
      .update({ status: "cancelled", updated_at: new Date().toISOString() })
      .eq("id", req.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    if (data) setRequests((prev) => prev.map((r) => r.id === req.id ? (data as DomainRequest) : r));
    setCancelling(null);
  };

  /* ── Derived state ── */
  const hasPending  = requests.some((r) => ["pending", "approved"].includes(r.status));
  const hasActive   = requests.some((r) => r.status === "active");
  const activeDomain = requests.find((r) => r.status === "active")?.requested_domain
    ?? (domainInfo?.custom_domain_status === "active" ? domainInfo?.custom_domain : null);
  const currentUrl  = tenantSlug
    ? `${window.location.origin}/s/${tenantSlug}`
    : `${window.location.origin}/s/your-slug`;

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <ModuleGate moduleId="custom_domain" moduleName="Custom Domain">
      <div className="space-y-5 max-w-2xl">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* ── Active domain banner ── */}
            {hasActive && activeDomain && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-100 shrink-0">
                  <i className="ri-global-line text-emerald-600 text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-emerald-700 font-semibold text-sm">Custom domain is live</p>
                  <p className="text-emerald-600 text-xs mt-0.5 font-mono truncate">{activeDomain}</p>
                </div>
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Active
                </span>
              </div>
            )}

            {/* ── Approved / DNS pending banner ── */}
            {requests.some((r) => r.status === "approved") && !hasActive && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-5 flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-sky-100 shrink-0">
                  <i className="ri-settings-line text-sky-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sky-700 font-semibold text-sm">Domain approved — DNS setup in progress</p>
                  <p className="text-sky-600 text-xs mt-1 leading-relaxed">
                    Your domain request was approved. DNS records will be shared with you. Once configured, your domain will go live.
                  </p>
                  <p className="text-sky-600 font-mono text-xs mt-1.5 font-semibold">
                    {requests.find((r) => r.status === "approved")?.requested_domain}
                  </p>
                </div>
              </div>
            )}

            {/* ── Current URL card ── */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 shrink-0">
                  <i className="ri-links-line text-slate-500 text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Your Current Public URL</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-slate-700 font-mono text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 truncate max-w-full">
                      {currentUrl}
                    </p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(currentUrl); }}
                      title="Copy URL"
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer shrink-0"
                    >
                      <i className="ri-clipboard-line text-sm"></i>
                    </button>
                  </div>
                  <p className="text-slate-400 text-xs mt-2">
                    A custom domain lets you use <strong>yourstudio.com</strong> instead of this path.
                  </p>
                </div>
              </div>
            </div>

            {/* ── Request form (only if no pending/active) ── */}
            {!hasPending && !hasActive && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8A96E]/10 shrink-0">
                    <i className="ri-global-line text-[#C8A96E] text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-slate-700 font-semibold text-sm mb-1">Request a Custom Domain</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">
                      Custom domain is an optional paid add-on. Once approved, you will receive DNS records
                      to connect your domain to this site.
                    </p>
                  </div>
                </div>

                {/* Pricing banner */}
                <div className="flex items-center gap-3 p-3.5 rounded-lg bg-[#C8A96E]/8 border border-[#C8A96E]/20 mb-5">
                  <span className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className="ri-price-tag-3-line text-[#C8A96E] text-base"></i>
                  </span>
                  <p className="text-slate-600 text-xs leading-relaxed">
                    <strong className="text-[#9a7840]">$10 / month</strong> — billed only after domain is verified and active.
                    No charge until approved.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
                      Domain <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-300 pointer-events-none">
                        <i className="ri-global-line text-sm"></i>
                      </span>
                      <input
                        type="text"
                        value={domain}
                        onChange={(e) => { setDomain(e.target.value); if (domainError) setDomainError(""); }}
                        placeholder="yourstudio.com"
                        className={`w-full bg-slate-50 border rounded-lg pl-9 pr-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none transition-all focus:bg-white ${
                          domainError
                            ? "border-red-300 focus:border-red-400"
                            : "border-slate-200 focus:border-[#C8A96E]/60"
                        }`}
                      />
                    </div>
                    {domainError ? (
                      <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {domainError}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-xs mt-1.5">
                        Examples: <span className="font-mono">yourstudio.com</span> · <span className="font-mono">studio.yourbrand.com</span>
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !domain.trim()}
                    className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Submit Request
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* ── How it works ── */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-4">How it works</h3>
              <div className="space-y-3">
                {[
                  { icon: "ri-edit-line",         title: "Submit your domain",    desc: "Enter the domain you own. We'll review it manually." },
                  { icon: "ri-shield-check-line",  title: "Review & approval",     desc: "Our team approves the request and prepares DNS records." },
                  { icon: "ri-settings-3-line",    title: "DNS configuration",     desc: "You'll receive DNS records to add at your registrar." },
                  { icon: "ri-global-line",        title: "Domain goes live",      desc: "Once DNS propagates (24–48h), your custom domain is active." },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 shrink-0 mt-0.5">
                      <i className={`${step.icon} text-[#C8A96E] text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-slate-600 text-xs font-medium">{step.title}</p>
                      <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Request history ── */}
            {requests.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-100">
                  <h3 className="text-slate-400 text-xs font-medium uppercase tracking-wider">Request History</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {requests.map((req) => {
                    const cfg = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending;
                    return (
                      <div key={req.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <div className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs shrink-0 mt-0.5 ${cfg.bg} ${cfg.border} ${cfg.color}`}>
                              <i className={cfg.icon}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="text-slate-700 text-sm font-semibold font-mono truncate">
                                {req.requested_domain}
                              </p>
                              <div className="flex items-center gap-2.5 mt-0.5 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${cfg.bg} ${cfg.border} ${cfg.color}`}>
                                  {cfg.label}
                                </span>
                                {req.price_monthly > 0 && (
                                  <span className="text-slate-400 text-xs">
                                    ${Number(req.price_monthly).toFixed(0)}/mo
                                  </span>
                                )}
                                <span className="text-slate-300 text-xs">
                                  {new Date(req.created_at).toLocaleDateString("en-US", {
                                    month: "short", day: "numeric", year: "numeric",
                                  })}
                                </span>
                              </div>
                              <p className="text-slate-400 text-xs mt-1">{cfg.desc}</p>

                              {/* Admin notes */}
                              {req.admin_notes && (
                                <div className="mt-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                                  <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Admin note</p>
                                  <p className="text-slate-600 text-xs leading-relaxed">{req.admin_notes}</p>
                                </div>
                              )}

                              {/* Approved: DNS hint */}
                              {req.status === "approved" && (
                                <div className="mt-2 p-2.5 rounded-lg bg-sky-50 border border-sky-200">
                                  <p className="text-sky-600 text-xs leading-relaxed">
                                    <i className="ri-information-line mr-1"></i>
                                    Your domain is approved. DNS configuration instructions will be shared via admin notes or email.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Cancel button (only pending) */}
                          {req.status === "pending" && (
                            <button
                              onClick={() => handleCancel(req)}
                              disabled={cancelling === req.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 bg-red-50 hover:bg-red-100 rounded-lg transition-all cursor-pointer disabled:opacity-40 whitespace-nowrap shrink-0"
                            >
                              {cancelling === req.id ? (
                                <div className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <i className="ri-close-line"></i>
                              )}
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ModuleGate>
  );
}